import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Joyride, ACTIONS, STATUS } from 'react-joyride';

import { useWalkthrough } from '../../contexts/WalkthroughContext';
import './Walkthrough.css';

/**
 * Bridge between WalkthroughContext and react-joyride v3.
 *
 * Design notes:
 *   - We run Joyride in UNCONTROLLED mode (no ``stepIndex`` prop). Joyride
 *     manages step navigation internally; we only react to lifecycle events.
 *     This is robust against re-renders and avoids the v2-era "controlled
 *     mode" bugs where stepIndex and Joyride state could desync.
 *   - v3 renamed the event hook from ``callback`` to ``onEvent`` and changed
 *     the handler signature to ``(data, controls)``. Several v2 top-level
 *     props (showProgress, spotlightPadding, scrollOffset, disableScrolling,
 *     etc.) moved into ``options``.
 *   - The body class ``walkthrough-active`` toggles the pulsing-glow CSS.
 *   - Colors come from the IMT palette in theme/tokens.css:
 *       brand green #4a6b25, brand dark #3a5b15, action orange #ff9900.
 */

const BRAND_PRIMARY = '#4a6b25';
const BRAND_DARK = '#3a5b15';
const BRAND_DEEP = '#2c4216';
const HMS_ACTION_ORANGE = '#ff9900';
const HMS_TEXT = '#1f2937';
const HMS_OVERLAY = 'rgba(44, 66, 22, 0.55)';

const joyrideStyles = {
    tooltip: {
        borderRadius: 12,
        // Padding lives on the inner sections (title/content/footer) so the
        // scrollable content area can claim the full inner width without
        // sliding under a fixed padding box.
        padding: 0,
        boxShadow: '0 12px 40px rgba(44, 66, 22, 0.25)',
        fontFamily: "'Lato', Arial, sans-serif",
        // 220px is sized to fit comfortably inside the 240px
        // scroll-margin-top reserved for tour targets in
        // Walkthrough.css. That guarantees a tooltip flipping upward
        // over a tall target (longer than the viewport) still fits
        // inside the viewport - the body region scrolls when content
        // exceeds this height. min() with viewport keeps shorter
        // laptops happy too.
        maxHeight: 'min(220px, calc(100vh - 32px))',
        maxWidth: 'min(420px, calc(100vw - 32px))',
        // Flex column lets the title/footer stay fixed-size while the
        // content area gets a real flex-basis to scroll inside.
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
    },
    tooltipContainer: {
        // The inner container Joyride wraps title + content + footer in.
        // Force it to fill the tooltip and allow children to shrink so
        // overflow on .tooltipContent actually triggers scrolling instead
        // of expanding the whole tooltip past max-height.
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        padding: '20px 20px 0 20px',
    },
    tooltipTitle: {
        color: BRAND_PRIMARY,
        fontSize: 16,
        fontWeight: 700,
        marginBottom: 8,
        flexShrink: 0,
    },
    tooltipContent: {
        color: HMS_TEXT,
        fontSize: 14,
        lineHeight: 1.55,
        padding: '4px 0 12px',
        // Scroll the BODY when content + title + buttons exceed the
        // tooltip's max-height. The buttons stay visible because they
        // live in the footer below this region.
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
    },
    tooltipFooter: {
        flexShrink: 0,
        padding: '0 20px 16px 20px',
    },
    buttonNext: {
        backgroundColor: BRAND_PRIMARY,
        color: '#ffffff',
        borderRadius: 8,
        padding: '8px 16px',
        fontSize: 13,
        fontWeight: 600,
        outline: 'none',
        border: '1px solid ' + BRAND_PRIMARY,
        boxShadow: '0 2px 8px rgba(74, 107, 37, 0.25)',
    },
    buttonBack: {
        color: BRAND_DARK,
        marginRight: 8,
        fontSize: 13,
        fontWeight: 500,
    },
    buttonSkip: {
        color: '#6b7280',
        fontSize: 13,
    },
    buttonClose: {
        color: '#6b7280',
        height: 14,
        width: 14,
    },
};

const joyrideOptions = {
    primaryColor: BRAND_PRIMARY,
    textColor: HMS_TEXT,
    backgroundColor: '#ffffff',
    overlayColor: HMS_OVERLAY,
    arrowColor: '#ffffff',
    zIndex: 11000,
    spotlightPadding: 6,
    spotlightRadius: 8,
    showProgress: true,
    // 180 gives floating-ui enough headroom that a tooltip flipping
    // upward over a near-top target (page header, stepper) still fits
    // inside the viewport instead of clipping off the top.
    scrollOffset: 180,
    // We own the scrolling: the engine's ``before`` hook calls
    // scrollIntoView({ block: 'center' }) on every targeted step so
    // the target lands in the middle of the viewport with room above
    // and below for the tooltip. Joyride's auto-scroll is not aware
    // of nested scroll containers (e.g. ``.hms-form-screen`` with
    // overflow-y:auto), which previously left the target off-screen
    // and the tooltip pinned to the viewport edge. ``skipScroll`` here
    // applies as a default to every step.
    skipScroll: true,
    // Click on the dark backdrop should NOT silently close - users can click
    // the tooltip's X button instead. Prevents accidental dismissals.
    overlayClickAction: false,
    // Cross-route steps need extra wait time for the destination component
    // tree to mount and render the target element after history.push().
    targetWaitTimeout: 4000,
};

const joyrideLocale = {
    back: 'Back',
    close: 'Close',
    last: 'Finish',
    next: 'Next',
    skip: 'Skip tour',
};

const WalkthroughEngine = () => {
    const history = useHistory();
    const location = useLocation();
    const { activeTour, stopTour, completeTour } = useWalkthrough();

    // Critical: Joyride must NOT mount until (a) we are on the tour's start
    // route, and (b) the DOM has had a tick to render the data-tour markers.
    // Otherwise Joyride hits TARGET_NOT_FOUND on the first few steps, fires
    // its auto-advance, and the tour silently jumps mid-way (e.g. starts at
    // step 7). ``tourReady`` gates the actual <Joyride> render below.
    const [tourReady, setTourReady] = useState(false);

    useEffect(() => {
        if (!activeTour) {
            setTourReady(false);
            return undefined;
        }

        // Tours can target routes with query strings (e.g.
        // /offer-generator?type=new&dealType=Bid). location.pathname does
        // NOT include the query, so we compare on pathname only - the
        // query is preserved by history.push.
        const startPathname = (activeTour.startRoute || '').split('?')[0];

        if (startPathname && location.pathname !== startPathname) {
            setTourReady(false);
            history.push(activeTour.startRoute);
            return undefined;
        }

        // We are on the start route. Give React one paint to commit the
        // page's data-tour attributes, then mount Joyride.
        const timer = setTimeout(() => setTourReady(true), 250);
        return () => clearTimeout(timer);
    }, [activeTour, location.pathname, history]);

    // Toggle a body class while a tour is running so the pulsing-glow CSS
    // (in Walkthrough.css) only fires during walkthroughs.
    useEffect(() => {
        if (activeTour) {
            document.body.classList.add('walkthrough-active');
            return () => document.body.classList.remove('walkthrough-active');
        }
        return undefined;
    }, [activeTour]);

    // Block click-outside-to-close handlers in the host app from firing
    // when the user interacts with Joyride's tooltip / overlay. Several
    // side panels (DocumentPanel, ChecklistPanel, InvoicePanel,
    // FinanceCalculator, DeliveryLogsModal) install a document-level
    // ``mousedown`` listener that calls ``onClose`` whenever a mousedown
    // lands outside their root. The Joyride tooltip and overlay live
    // outside those panels, so without this guard every Next button
    // press would close the panel that the current step is targeting -
    // tooltip then loses its anchor and jumps to the upper-left corner
    // until the next click. We intercept on the capture phase and stop
    // propagation so the bubble-phase listeners on document never see
    // the event. Joyride's own buttons use ``click`` (not mousedown)
    // for navigation, so this does not break tour controls.
    useEffect(() => {
        if (!activeTour) return undefined;
        const isInsideJoyride = (target) => {
            if (!(target instanceof Element)) return false;
            return Boolean(
                target.closest('.react-joyride__tooltip')
                || target.closest('.__floater__')
                || target.closest('.react-joyride__overlay')
                || target.closest('.react-joyride__spotlight')
            );
        };
        const blockOutsideClose = (event) => {
            if (isInsideJoyride(event.target)) {
                event.stopPropagation();
            }
        };
        document.addEventListener('mousedown', blockOutsideClose, true);
        document.addEventListener('pointerdown', blockOutsideClose, true);
        document.addEventListener('touchstart', blockOutsideClose, true);
        return () => {
            document.removeEventListener('mousedown', blockOutsideClose, true);
            document.removeEventListener('pointerdown', blockOutsideClose, true);
            document.removeEventListener('touchstart', blockOutsideClose, true);
        };
    }, [activeTour]);

    // Lock user scrolling while the tour runs. We block wheel/touchmove
    // events instead of using ``overflow: hidden`` so that Joyride's
    // programmatic scrollIntoView still works between steps. Scrolling
    // inside the tooltip itself is preserved (e.g., long content).
    useEffect(() => {
        if (!activeTour) return undefined;

        const isInsideTooltip = (target) => {
            if (!(target instanceof Element)) return false;
            return Boolean(
                target.closest('.react-joyride__tooltip') ||
                target.closest('.__floater__') ||
                target.closest('.walkthrough-modal')
            );
        };

        const prevent = (event) => {
            if (isInsideTooltip(event.target)) return;
            event.preventDefault();
        };

        document.addEventListener('wheel', prevent, { passive: false });
        document.addEventListener('touchmove', prevent, { passive: false });
        // Block keyboard scroll triggers (Space, Page Up/Down, arrows on body).
        const preventKeys = (event) => {
            const blocked = [' ', 'PageDown', 'PageUp', 'End', 'Home', 'ArrowDown', 'ArrowUp'];
            if (blocked.includes(event.key) && event.target === document.body) {
                event.preventDefault();
            }
        };
        document.addEventListener('keydown', preventKeys);

        return () => {
            document.removeEventListener('wheel', prevent);
            document.removeEventListener('touchmove', prevent);
            document.removeEventListener('keydown', preventKeys);
        };
    }, [activeTour]);

    // Resolve which route a given step should run on. Falls back to the
    // tour-level startRoute so most steps don't have to repeat it.
    const routeForStep = (step) => step.route || activeTour?.startRoute;

    const steps = useMemo(() => {
        if (!activeTour) return [];

        return activeTour.steps
            .filter((step) => {
                // ``skipIfMissing`` is for permission-gated steps whose
                // targets are PERMANENTLY absent for some users (e.g. the
                // Import Partners button only renders for CRM approvers).
                // It is NOT for steps whose targets only appear after an
                // interaction.
                if (!step.skipIfMissing) return true;
                // Steps with a clickBefore are expected to materialize
                // their own target by triggering opens (panels, modals,
                // tabs) before the step shows. Filtering those at tour
                // start would drop them unconditionally because the
                // panel they target has not been opened yet. Keep them.
                if (step.clickBefore) return true;
                if (routeForStep(step) !== activeTour.startRoute) return true;
                if (typeof step.target !== 'string') return true;
                return Boolean(document.querySelector(step.target));
            })
            .map((step) => {
                const targetRoute = routeForStep(step);

                return {
                    target: step.target,
                    title: step.title,
                    content: step.content,
                    placement: step.placement || 'auto',
                    skipBeacon: step.disableBeacon ?? true,
                    // Per-step before hook handles route changes AND in-page
                    // state changes (e.g. switching tabs in Cargo/Bunkers).
                    // v3 awaits this promise before showing the step, and
                    // ``targetWaitTimeout`` (in options) gives the destination
                    // time to mount its DOM.
                    before: async () => {
                        // Compare on pathname only - per-step routes can carry
                        // query strings (e.g. ?type=new&dealType=Bid) which
                        // window.location.pathname strips. history.push keeps
                        // the full route so the destination form sees the query.
                        const targetPathname = (targetRoute || '').split('?')[0];
                        if (targetPathname && window.location.pathname !== targetPathname) {
                            history.push(targetRoute);
                            // Brief settle delay so React commits the new tree
                            // before Joyride starts looking for the target.
                            await new Promise((resolve) => setTimeout(resolve, 250));
                        }
                        // ``clickBefore`` simulates one or more user clicks
                        // before the step shows. A single string clicks one
                        // selector; an array clicks each in order with a
                        // small delay between them. Sequential clicks are
                        // how multi-step interactions get set up - e.g.
                        // close the previous side panel, open the action
                        // menu, then click a menu item to open the next
                        // panel.
                        if (step.clickBefore) {
                            const selectors = Array.isArray(step.clickBefore)
                                ? step.clickBefore
                                : [step.clickBefore];
                            for (const selector of selectors) {
                                const el = document.querySelector(selector);
                                if (el && typeof el.click === 'function') {
                                    el.click();
                                    // 250ms covers React state propagation
                                    // plus any short panel-close/open
                                    // animation before the next click fires.
                                    // eslint-disable-next-line no-await-in-loop
                                    await new Promise((resolve) => setTimeout(resolve, 250));
                                }
                            }
                        }
                        // Manual scroll: bring the target into the viewport
                        // so floating-ui has somewhere visible to anchor the
                        // tooltip. We skip this for centre-placed and
                        // body-targeted steps since they do not need a
                        // positioned anchor.
                        //
                        // The scroll strategy depends on the target's height:
                        //   - Short target (fits in viewport): centre it. The
                        //     tooltip can flip top OR bottom and still fit.
                        //   - Tall target (taller than ~70% of viewport):
                        //     centring would place the top edge far above
                        //     the viewport, and a placement: 'top' tooltip
                        //     would anchor to that off-screen point. Use
                        //     block: 'start' so the top sits at the CSS
                        //     scroll-margin-top offset (defined in
                        //     Walkthrough.css), leaving room above the
                        //     target for the tooltip.
                        const isPositionedTarget =
                            typeof step.target === 'string'
                            && step.target !== 'body'
                            && step.placement !== 'center';
                        if (isPositionedTarget) {
                            const targetEl = document.querySelector(step.target);
                            if (targetEl && typeof targetEl.scrollIntoView === 'function') {
                                const rect = targetEl.getBoundingClientRect();
                                const viewportHeight =
                                    window.innerHeight
                                    || document.documentElement.clientHeight
                                    || 0;
                                const isTall =
                                    viewportHeight > 0
                                    && rect.height > viewportHeight * 0.7;
                                targetEl.scrollIntoView({
                                    behavior: 'smooth',
                                    block: isTall ? 'start' : 'center',
                                });
                                // 400ms covers smooth scroll + layout reflow
                                // before Joyride measures the target rect.
                                await new Promise((resolve) => setTimeout(resolve, 400));
                            }
                        }
                    },
                };
            });
        // location.pathname intentionally not in deps - we resolve it via
        // window.location inside the before hook to always read live state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTour, history]);

    // v3 onEvent handler: (data, controls) => void. We only need lifecycle
    // signals - Joyride owns step navigation. Status/action are enough to
    // distinguish "user finished" from "user skipped/closed".
    const handleEvent = (data) => {
        const { status, action } = data;

        if (status === STATUS.FINISHED) {
            completeTour();
            return;
        }

        if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE || action === ACTIONS.STOP) {
            stopTour();
        }
    };

    if (!activeTour || !tourReady) {
        return null;
    }

    return (
        <Joyride
            steps={steps}
            run
            continuous
            scrollToFirstStep
            options={joyrideOptions}
            styles={joyrideStyles}
            locale={joyrideLocale}
            onEvent={handleEvent}
        />
    );
};

export default WalkthroughEngine;
